/**
 * God Mode's inline editor for one Team line (2026-10-05): on or off,
 * Authority, the daily limit, and the clock's times, days and timezone. The
 * Authority here is an override of the Team's ladder step (decision 7,
 * 2026-10-19): customers pick one step for the whole Team and never see a
 * per-line rung. The server saves through the same rules as the customer's Team page, so this
 * only shapes the request and says what the server refused.
 *
 * Also the Team's build lane (spec 2026-10-22, decision 9): one select per Team, not per line,
 * and the workspace's "Build lane for every Team" that overrides every Team's row.
 */
import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import type {
  AdminTeamLineControls,
  AdminTeamLinePatch,
  AdminWorkspaceTeamsResponse,
  JobAuthority,
  ScheduleDay,
  TeamLane,
} from "@yagni/shared";
import { isTeamLane, TEAM_LANES } from "@yagni/shared";

const DAYS: ReadonlyArray<{ value: ScheduleDay; label: string }> = [
  { value: "mon", label: "Mon" },
  { value: "tue", label: "Tue" },
  { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" },
  { value: "fri", label: "Fri" },
  { value: "sat", label: "Sat" },
  { value: "sun", label: "Sun" },
];

const TIME_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;

function parseTimes(raw: string): string[] | null {
  const times = raw
    .split(/[,\s]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
  if (times.length === 0 || times.some((entry) => !TIME_RE.test(entry))) return null;
  return times.map((entry) => entry.padStart(5, "0"));
}

function isTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((entry, index) => entry === b[index]);
}

const inputStyle = {
  borderColor: "var(--border)",
  background: "var(--bg-input)",
  color: "var(--text-primary)",
} as const;

const LANE_LABELS: Record<TeamLane, string> = {
  legacy: "Legacy",
  attempt: "One attempt",
  session: "Session",
};

const LANE_SAVE_TIMEOUT_MS = 30_000;

interface LaneSelectProps {
  label: string;
  lane: TeamLane;
  help: string;
  testId: string;
  /** Sends the change; resolves with the server's response, which the caller reads. */
  send: (next: TeamLane, signal: AbortSignal) => Promise<Response>;
  /** A 2xx answer to `send(next)`. */
  onSaved: (res: Response, next: TeamLane) => Promise<void>;
  /** Nothing to change to: the select is shown but cannot be moved. */
  disabled?: boolean;
}

/**
 * A build lane select that saves on change. It always shows the lane the
 * server last answered with, so a refusal leaves it where it was.
 */
function LaneSelect({ label, lane, help, testId, send, onSaved, disabled }: LaneSelectProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (next: TeamLane) => {
    if (next === lane) return;
    setBusy(true);
    setError(null);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LANE_SAVE_TIMEOUT_MS);
    try {
      const res = await send(next, controller.signal);
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setError(body?.error ?? `Request failed (${res.status})`);
        return;
      }
      await onSaved(res, next);
    } catch (err) {
      setError(
        controller.signal.aborted
          ? "The save did not answer in time. Reload the Teams to see whether it landed."
          : err instanceof Error
            ? err.message
            : "Failed to set the build lane",
      );
    } finally {
      clearTimeout(timer);
      setBusy(false);
    }
  };

  return (
    <div
      className="flex items-center gap-2 flex-wrap text-[12px] mt-1"
      style={{ color: "var(--text-secondary)" }}
      data-testid={testId}
    >
      <label className="flex items-center gap-2">
        <span>{label}</span>
        <select
          value={lane}
          disabled={busy || disabled}
          onChange={(e) => {
            if (isTeamLane(e.target.value)) void save(e.target.value);
          }}
          aria-label={label}
          className="px-2 py-1 rounded border text-[12px]"
          style={inputStyle}
        >
          {TEAM_LANES.map((value) => (
            <option key={value} value={value}>
              {LANE_LABELS[value]}
            </option>
          ))}
        </select>
      </label>
      {busy && <Loader2 size={10} className="animate-spin" />}
      <span>{help}</span>
      {error && (
        <p role="alert" className="basis-full" style={{ color: "var(--error)" }}>
          {error}
        </p>
      )}
    </div>
  );
}

interface TeamLaneSelectProps {
  workspaceId: string;
  deskId: string;
  lane: TeamLane;
  /**
   * The lane the workspace's "Build lane for every Team" puts this Team on
   * over its row, or null when the row decides ({@link workspaceOverride}).
   */
  overriddenBy: TeamLane | null;
  /** The refreshed Teams overview the lane route answers with. */
  onSaved: (overview: AdminWorkspaceTeamsResponse) => void;
}

/**
 * The lane the workspace's own lane puts a Team on over its row, or null when
 * the row decides: the backend's rule (`attempts/lane.ts` `teamBuildLane`).
 * Session overrides every row; One attempt every row but a Session one.
 */
export function workspaceOverride(workspace: TeamLane, row: TeamLane): TeamLane | null {
  if (workspace === "session") return row === "session" ? null : "session";
  if (workspace === "attempt") return row === "legacy" ? "attempt" : null;
  return null;
}

/** Which lane a Team's new tickets start on, while the workspace switch leaves it to the Team. */
export function TeamLaneSelect({
  workspaceId,
  deskId,
  lane,
  overriddenBy,
  onSaved,
}: TeamLaneSelectProps) {
  return (
    <LaneSelect
      label="Build lane"
      lane={lane}
      testId={`team-lane-${deskId}`}
      help={
        overriddenBy
          ? `The workspace switch puts this Team on ${LANE_LABELS[overriddenBy]}; this row decides again once the switch is back on Legacy.`
          : "New tickets start on this lane. A ticket already started finishes on the lane it started on."
      }
      send={(next, signal) =>
        fetch(
          `/api/admin/workspace-controls/${encodeURIComponent(workspaceId)}/teams/${encodeURIComponent(deskId)}/lane`,
          {
            method: "PATCH",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ lane: next }),
            signal,
          },
        )
      }
      onSaved={async (res) => onSaved((await res.json()) as AdminWorkspaceTeamsResponse)}
    />
  );
}

const WORKSPACE_LANE_HELP =
  "On One attempt, every Team in this workspace starts new tickets on the one-attempt lane, overriding each Team's own Build lane row except a Session one. On Session, every Team works each ticket as one session in one Machine.";

interface WorkspaceLaneSelectProps {
  workspaceId: string;
  lane: TeamLane;
  /**
   * Why this workspace cannot go on the lane (its builds run on Fly Machines,
   * and this deployment has no Machines runner), or null.
   */
  refusal: string | null;
  /** The lane just saved. */
  onSaved: (lane: TeamLane) => void;
}

/**
 * The workspace's "Build lane for every Team" (Jack, 2026-10-23 and
 * 2026-10-28): One attempt and Legacy are the `build.one_attempt_lane` flag's
 * workspace override, Session the workspace's own session switch, all saved
 * through the workspace controls' build-lane route, which keeps the two in
 * step.
 */
export function WorkspaceLaneSelect({
  workspaceId,
  lane,
  refusal,
  onSaved,
}: WorkspaceLaneSelectProps) {
  return (
    <LaneSelect
      label="Build lane for every Team"
      lane={lane}
      testId="workspace-lane"
      help={refusal ? `${WORKSPACE_LANE_HELP} ${refusal}` : WORKSPACE_LANE_HELP}
      // A workspace the lane cannot run for has nowhere to move from Legacy;
      // one already on One attempt can still be moved back.
      disabled={refusal !== null && lane === "legacy"}
      send={(next, signal) =>
        fetch(`/api/admin/workspace-controls/${encodeURIComponent(workspaceId)}/build-lane`, {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ lane: next }),
          signal,
        })
      }
      // The route answers 2xx only once the lane is stored, so the lane sent
      // is the lane saved, whatever the body carries.
      onSaved={async (_res, next) => onSaved(next)}
    />
  );
}

interface Props {
  jobId: string;
  active: boolean;
  controls: AdminTeamLineControls;
  /** Resolves to an error sentence, or null when the save landed. */
  onSave: (patch: AdminTeamLinePatch) => Promise<string | null>;
  onClose: () => void;
}

export function TeamLineControls({ jobId, active, controls, onSave, onClose }: Props) {
  const [on, setOn] = useState(active);
  const [authority, setAuthority] = useState<JobAuthority | null>(controls.authority);
  const [perDay, setPerDay] = useState(
    controls.perDay?.value == null ? "" : String(controls.perDay.value),
  );
  const initialTimes = controls.clock?.times.join(", ") ?? "";
  const [times, setTimes] = useState(initialTimes);
  const [days, setDays] = useState<ScheduleDay[]>(controls.clock?.days ?? []);
  const [timezone, setTimezone] = useState(controls.clock?.timezone ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { patch, problem } = useMemo(() => {
    const out: AdminTeamLinePatch = {};
    if (on !== active) out.active = on;
    if (authority !== null && authority !== controls.authority) out.authority = authority;

    if (controls.perDay) {
      const trimmed = perDay.trim();
      const value = trimmed === "" ? null : Number(trimmed);
      if (
        value !== null &&
        (!Number.isInteger(value) || value < controls.perDay.min || value > controls.perDay.max)
      ) {
        return {
          patch: out,
          problem: `The daily limit is a whole number from ${controls.perDay.min} to ${controls.perDay.max}, or empty for the default.`,
        };
      }
      if (value !== controls.perDay.value) out.perDay = value;
    }

    if (controls.clock) {
      if (times.trim() !== initialTimes.trim()) {
        const parsed = parseTimes(times);
        if (!parsed)
          return {
            patch: out,
            problem: "Times are HH:MM, separated by commas, like 09:00, 14:00.",
          };
        if (!sameList(parsed, controls.clock.times)) out.times = parsed;
      }
      const order = DAYS.map((day) => day.value);
      const ordered = order.filter((day) => days.includes(day));
      const stored = order.filter((day) => controls.clock?.days.includes(day));
      if (!sameList(ordered, stored)) {
        if (ordered.length === 0) return { patch: out, problem: "Pick at least one day." };
        out.days = ordered;
      }
      const zone = timezone.trim();
      if (zone !== "" && !isTimeZone(zone)) {
        return { patch: out, problem: "The timezone must be an IANA zone such as America/Denver." };
      }
      const nextZone = zone === "" ? null : zone;
      if (nextZone !== controls.clock.timezone) out.timezone = nextZone;
    }
    return { patch: out, problem: null };
  }, [on, active, authority, controls, perDay, times, initialTimes, days, timezone]);

  const changed = Object.keys(patch).length > 0;

  const save = async () => {
    setBusy(true);
    setError(null);
    const refused = await onSave(patch);
    setBusy(false);
    if (refused) setError(refused);
    else onClose();
  };

  return (
    <div
      className="mt-1 mb-2 p-2 rounded border space-y-2 text-[12px]"
      style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}
      data-testid={`line-controls-${jobId}`}
    >
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => setOn(e.target.checked)}
          aria-label="Line is on"
        />
        On
      </label>

      {controls.authority === null ? (
        <p>This line runs the Team's own machinery, so it has no Authority to set.</p>
      ) : (
        <label className="flex items-center gap-2 flex-wrap">
          <span>Authority (override)</span>
          <select
            value={authority ?? ""}
            onChange={(e) => setAuthority(e.target.value as JobAuthority)}
            aria-label="Authority"
            className="px-2 py-1 rounded border text-[12px]"
            style={inputStyle}
          >
            <option value="supervised">Supervised</option>
            <option value="autonomous" disabled={controls.ceiling !== "autonomous"}>
              Autonomous{controls.ceiling !== "autonomous" ? " (not allowed for this line)" : ""}
            </option>
          </select>
          {authority === "supervised" && controls.authority !== "supervised" && (
            <span>The Team owner supervises unless the line already names someone.</span>
          )}
          <span className="basis-full" data-testid={`line-controls-override-${jobId}`}>
            Overrides the Team's ladder step for this line only. Team settings then reads Custom
            (override) until someone picks a step again.
          </span>
        </label>
      )}

      {controls.perDay && (
        <label className="flex items-center gap-2 flex-wrap">
          <span>Daily limit</span>
          <input
            type="number"
            min={controls.perDay.min}
            max={controls.perDay.max}
            step="1"
            value={perDay}
            placeholder={String(controls.perDay.defaultValue)}
            onChange={(e) => setPerDay(e.target.value)}
            aria-label="Daily limit"
            className="px-2 py-1 rounded border text-[12px] w-20"
            style={inputStyle}
          />
          <span>
            tickets a day. Empty uses the default ({controls.perDay.defaultValue})
            {controls.perDay.autonomousOnly ? ". Counts only while the line is autonomous." : "."}
          </span>
        </label>
      )}

      {controls.clock && (
        <div className="space-y-2">
          {controls.clock.every && (
            <p>
              Runs every {controls.clock.every.minutes} minutes now. Setting times replaces the
              interval.
            </p>
          )}
          <label className="flex items-center gap-2 flex-wrap">
            <span>Times</span>
            <input
              type="text"
              value={times}
              placeholder="09:00, 14:00"
              onChange={(e) => setTimes(e.target.value)}
              aria-label="Times"
              className="px-2 py-1 rounded border text-[12px] w-48"
              style={inputStyle}
            />
          </label>
          <fieldset className="flex items-center gap-2 flex-wrap">
            <legend className="sr-only">Days</legend>
            <span>Days</span>
            {DAYS.map((day) => (
              <label key={day.value} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={days.includes(day.value)}
                  onChange={(e) =>
                    setDays((prev) =>
                      e.target.checked
                        ? [...prev, day.value]
                        : prev.filter((entry) => entry !== day.value),
                    )
                  }
                  aria-label={day.label}
                />
                {day.label}
              </label>
            ))}
          </fieldset>
          <label className="flex items-center gap-2 flex-wrap">
            <span>Timezone</span>
            <input
              type="text"
              value={timezone}
              placeholder="The workspace's timezone"
              onChange={(e) => setTimezone(e.target.value)}
              aria-label="Timezone"
              className="px-2 py-1 rounded border text-[12px] w-48"
              style={inputStyle}
            />
          </label>
        </div>
      )}

      {(problem || error) && (
        <p role="alert" style={{ color: "var(--error)" }}>
          {problem ?? error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy || !changed || problem !== null}
          onClick={() => void save()}
          className="px-2 py-1 rounded text-[11px] border-none cursor-pointer disabled:opacity-50"
          style={{ background: "var(--accent-muted)", color: "var(--accent)" }}
        >
          {busy ? <Loader2 size={10} className="animate-spin" /> : "Save line"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-2 py-1 rounded text-[11px] border-none cursor-pointer"
          style={{ background: "rgba(var(--ink-rgb), 0.06)", color: "var(--text-secondary)" }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
